import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, EmptyState, FormField, TextInput, PageHeader, Tabs, Toggle } from '@/components/ui';
import { agentApi } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';
import { useAuthStore } from '@/shared/stores/auth';
import { canView } from '@/shared/lib/permissions';
import { AudioDeviceSettings } from '../components/AudioDeviceSettings';

export function Settings() {
  const user = useAuthStore((s) => s.user);
  // תסריטים עברו מלשונית-צד ללשונית כאן — נשמרת אותה הרשאת מסך (scripts).
  const showScripts = canView(user, 'scripts');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = showScripts && searchParams.get('tab') === 'scripts' ? 'scripts' : 'general';

  return (
    <div className="stack" style={{ padding: 'var(--gap)', maxWidth: 640 }}>
      <PageHeader title="הגדרות אישיות" />
      {showScripts && (
        <Tabs
          items={[
            { id: 'general', label: 'כללי' },
            { id: 'scripts', label: 'תסריטי שיחה' },
          ]}
          value={tab}
          onChange={(id) => setSearchParams(id === 'general' ? {} : { tab: id }, { replace: true })}
        />
      )}
      {tab === 'scripts' ? <ScriptsTab /> : <GeneralTab />}
    </div>
  );
}

function GeneralTab() {
  const [profile, setProfile] = useState<{ name?: string; email?: string; phone?: string; extension?: string; locale?: string } | null>(null);
  const [presence, setPresence] = useState<string>('offline');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // העברה לנייד: נשמר בנפרד מ"טלפון" בפרופיל — זה פרט קשר, וזו
  // הוראה למרכזייה לחייג. ערבוב היה מנתב שיחות למספר שהוזן כפרט קשר.
  const [fwdEnabled, setFwdEnabled] = useState(false);
  const [fwdNumber, setFwdNumber] = useState('');
  const [fwdSaving, setFwdSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      const data = (await agentApi.settings()) as {
        profile?: { name?: string; phone?: string; email?: string; extension?: string; locale?: string };
        presence?: { status?: string };
        preferences?: { mobile_forward?: { enabled?: boolean; number?: string | null } };
      };
      setProfile(data.profile ?? null);
      setName(data.profile?.name ?? '');
      setPhone(data.profile?.phone ?? '');
      setPresence(data.presence?.status ?? 'offline');
      setFwdEnabled(data.preferences?.mobile_forward?.enabled ?? false);
      setFwdNumber(data.preferences?.mobile_forward?.number ?? '');
    })();
  }, []);

  async function save() {
    try {
      const { apiClient } = await import('@/shared/api/client');
      await apiClient.patch('/agent/settings', { name, phone });
      toast('נשמר', 'ok');
    } catch {
      toast('שגיאה בשמירת ההגדרות', 'danger');
    }
  }

  async function saveForward(enabled: boolean, number: string) {
    setFwdSaving(true);
    try {
      const { apiClient } = await import('@/shared/api/client');
      await apiClient.put('/agent/settings/mobile-forward', { enabled, number: number || null });
      setFwdEnabled(enabled);
      toast(enabled ? 'העברה לנייד הופעלה' : 'העברה לנייד כובתה', 'ok');
    } catch (e) {
      // השרת מחזיר 422 עם הסבר כשהמספר אינו נייד ישראלי תקין —
      // מציגים אותו כמו שהוא במקום הודעה גנרית.
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירת ההעברה', 'danger');
      setFwdEnabled(!enabled ? false : fwdEnabled);
    } finally {
      setFwdSaving(false);
    }
  }

  async function changePresence(s: string) {
    try {
      // דרך ה-store: "מנותק" גם מנתק את קו ה-SIP (והחזרה רושמת מחדש).
      const { useSipStore } = await import('../stores/sipStore');
      await useSipStore.getState().setPresence(s);
      setPresence(s);
    } catch {
      toast('שגיאה בעדכון זמינות', 'danger');
    }
  }

  return (
    <>
      <Card title="פרופיל">
        <div className="stack">
          <FormField label="שם">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="אימייל">
            <TextInput value={profile?.email ?? ''} disabled dir="ltr" />
          </FormField>
          <FormField label="טלפון">
            <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" />
          </FormField>
          <FormField label="שלוחה">
            <TextInput value={profile?.extension ?? ''} disabled dir="ltr" />
          </FormField>
          <div>
            <button type="button" className="btn primary" onClick={save}>שמור</button>
          </div>
        </div>
      </Card>

      <Card title="העברה לנייד" subtitle="כשמתקשרים לשלוחה שלך, הנייד יצלצל במקביל">
        <div className="stack">
          <FormField
            label="מספר הנייד"
            hint="נייד ישראלי (05X-XXXXXXX). השיחה תוקלט ותתומלל כרגיל."
          >
            <TextInput
              value={fwdNumber}
              onChange={(e) => setFwdNumber(e.target.value)}
              dir="ltr"
              inputMode="tel"
              placeholder="050-000-0000"
            />
          </FormField>
          <Toggle
            checked={fwdEnabled}
            disabled={fwdSaving}
            onChange={(next) => void saveForward(next, fwdNumber)}
            label="הפעל העברה לנייד"
          />
          <div>
            <button
              type="button"
              className="btn"
              disabled={fwdSaving}
              onClick={() => void saveForward(fwdEnabled, fwdNumber)}
            >
              שמור מספר
            </button>
          </div>
        </div>
      </Card>

      <Card title="זמינות">
        {/* Single-select: presence is one state, so a radio-pill group — not
            six independent toggles that read as multi-select. */}
        <div role="radiogroup" aria-label="זמינות הנציג" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(['available', 'oncall', 'acw', 'break', 'lunch', 'paperwork', 'training', 'offline'] as const).map((s) => {
            const active = presence === s;
            const tone = PRESENCE_TONE[s];
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => changePresence(s)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 'var(--r-pill)',
                  border: `1px solid ${active ? 'transparent' : 'var(--line)'}`,
                  background: active ? (tone === 'muted' ? 'var(--surface-2)' : `var(--${tone}-soft)`) : 'var(--surface)',
                  color: active ? (tone === 'muted' ? 'var(--text-2)' : `var(--${tone})`) : 'var(--text-2)',
                  fontWeight: active ? 600 : 500, fontSize: 'var(--t-sm)', cursor: 'pointer',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: `var(--${tone})` }} />
                {labelFor(s)}
              </button>
            );
          })}
        </div>
      </Card>

      <Card title="מיקרופון ורמקול">
        <AudioDeviceSettings />
      </Card>
    </>
  );
}

interface Script {
  id: number;
  name: string;
  description?: string;
}

/** תסריטי שיחה — לשעבר מסך /agent/scripts, עכשיו לשונית בהגדרות. */
function ScriptsTab() {
  const [items, setItems] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void agentApi.scripts().then((d) => {
      setItems(d as Script[]);
      setLoading(false);
    });
  }, []);

  if (loading) return <Card>טוען…</Card>;
  if (!items.length) return <EmptyState icon="book" title="אין תסריטי שיחה זמינים" />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)' }}>
      {items.map((s) => (
        <Card key={s.id} title={s.name}>
          <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 'var(--t-sm)' }}>
            {s.description ?? 'תסריט שיחה.'}
          </p>
        </Card>
      ))}
    </div>
  );
}

const PRESENCE_TONE: Record<string, string> = {
  available: 'ok', oncall: 'info', acw: 'warn', break: 'warn', lunch: 'warn', paperwork: 'warn', training: 'busy', offline: 'muted',
};

function labelFor(s: string) {
  return {
    available: 'זמין',
    oncall: 'בשיחה',
    acw: 'אחרי שיחה',
    break: 'הפסקה',
    lunch: 'ארוחת צהריים',
    paperwork: 'עבודת ניירת',
    training: 'הכשרה',
    offline: 'מנותק',
  }[s] ?? s;
}
